import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Building2, Calendar as CalendarIcon, ChevronLeft, ChevronRight, ExternalLink,
  Instagram, Facebook, Music2, MessageCircle, Mail, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const PLATFORM_ICON = {
  instagram: Instagram,
  facebook: Facebook,
  tiktok: Music2,
  whatsapp: MessageCircle,
  email: Mail,
};

const STATUS_LABELS = {
  draft: "Borrador", scheduled: "Programada", published: "Publicada",
  failed: "Fallida", sending: "Enviando", sent: "Enviada",
};
const STATUS_STYLES = {
  draft: "bg-stone-100 text-stone-700 border-stone-200",
  scheduled: "bg-brand-gold-soft text-brand-gold-dark border-brand-gold/40",
  published: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30",
  sent: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/30",
  sending: "bg-blue-50 text-blue-700 border-blue-200",
  failed: "bg-destructive/10 text-destructive border-destructive/30",
};

const MONTH_NAMES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const WEEKDAYS = ["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"];

function monthGrid(y, m) {
  const first = new Date(y, m, 1);
  const dow = (first.getDay() + 6) % 7;
  const start = new Date(y, m, 1 - dow);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}
function sameDay(a, b) { return a.getFullYear()===b.getFullYear()&&a.getMonth()===b.getMonth()&&a.getDate()===b.getDate(); }

export default function CrossCompanyCalendar() {
  const [cursor, setCursor] = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [data, setData] = useState({ companies: [], events: [] });
  const [loading, setLoading] = useState(true);
  const [companyFilter, setCompanyFilter] = useState("all");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const monthStart = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth(), 1), [cursor]);
  const monthEnd = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59), [cursor]);
  const days = useMemo(() => monthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  const load = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const params = { start: monthStart.toISOString(), end: monthEnd.toISOString() };
      const { data: resp } = await api.get("/calendar/cross-company", { params });
      setData(resp);
    } catch (e) {
      if (e.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar calendario");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [monthStart.getTime()]);

  const filteredEvents = useMemo(() => {
    if (companyFilter === "all") return data.events;
    return data.events.filter((e) => e.company_id === companyFilter);
  }, [data.events, companyFilter]);

  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const e of filteredEvents) {
      if (!e.at) continue;
      const d = new Date(e.at);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    return map;
  }, [filteredEvents]);

  const today = new Date();

  if (accessDenied) {
    return (
      <div className="max-w-2xl mx-auto card-elevated p-16 text-center" data-testid="cross-company-access-denied">
        <Building2 className="h-12 w-12 mx-auto text-stone-400 mb-4" />
        <h2 className="font-serif-display text-2xl text-brand-ink font-semibold mb-2">
          Sin acceso al calendario multi-empresa
        </h2>
        <p className="text-sm text-stone-500">
          Esta vista está reservada al rol Master y a Administradores con empresas autorizadas.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="cross-company-calendar-page">
      <div>
        <div className="divider-gold mb-3" />
        <h1 className="font-serif-display text-4xl text-brand-ink font-semibold flex items-center gap-3">
          <Building2 className="h-8 w-8 text-brand-emerald" />
          Calendario Multi-Empresa
        </h1>
        <p className="text-sm text-stone-500 mt-2">
          Vista consolidada de todas las publicaciones y campañas de las empresas autorizadas.
          Cada evento está coloreado según la empresa de origen.
        </p>
      </div>

      {/* Company legend */}
      {data.companies?.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="cross-company-legend">
          {data.companies.map((c) => (
            <button
              key={c.id}
              onClick={() => setCompanyFilter(companyFilter === c.id ? "all" : c.id)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-opacity ${
                companyFilter === c.id ? "border-brand-gold shadow-gold" : "border-border hover:opacity-80"
              }`}
              data-testid={`legend-company-${c.id}`}
            >
              <span
                className="h-4 w-4 rounded flex items-center justify-center text-white text-[9px] font-bold"
                style={{ backgroundColor: c.color }}
              >
                {c.initial}
              </span>
              {c.name}
            </button>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="xcc-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="font-serif-display text-2xl text-brand-ink font-semibold min-w-[220px] text-center">
            {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
          </div>
          <Button variant="outline" size="icon" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="xcc-next-month">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} data-testid="xcc-today">
            Hoy
          </Button>
        </div>
        <div className="w-56">
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger data-testid="xcc-company-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="xcc-company-filter-all">Todas las empresas</SelectItem>
              {data.companies?.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`xcc-company-filter-${c.id}`}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="card-elevated p-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" /></div>
      ) : (
        <>
        <div className="hidden md:block card-elevated overflow-hidden">
          <div className="grid grid-cols-7 border-b border-border bg-stone-50/60">
            {WEEKDAYS.map((w) => (
              <div key={w} className="px-3 py-2 text-xs font-semibold text-stone-500 uppercase tracking-widest text-center">{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 auto-rows-[minmax(120px,auto)]">
            {days.map((d, idx) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = sameDay(d, today);
              const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
              const dayEvents = eventsByDay.get(key) || [];
              return (
                <div
                  key={idx}
                  data-testid={`xcc-cell-${d.toISOString().slice(0, 10)}`}
                  className={`p-2 border-b border-r border-border ${inMonth ? "bg-white" : "bg-stone-50/30"} ${isToday ? "ring-2 ring-inset ring-brand-gold" : ""}`}
                >
                  <div className={`text-xs font-semibold ${inMonth ? "text-brand-ink" : "text-stone-300"} mb-1`}>
                    {d.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((e) => {
                      const Icon = PLATFORM_ICON[e.platform] || Mail;
                      const color = e.company_color || "#00A36C";
                      return (
                        <button
                          key={e.id}
                          onClick={() => setSelectedEvent(e)}
                          className="w-full text-left rounded px-1.5 py-1 text-[11px] leading-tight hover:opacity-80 flex items-center gap-1"
                          style={{ backgroundColor: `${color}18`, color, borderLeft: `3px solid ${color}` }}
                          data-testid={`xcc-event-${e.id}`}
                          title={`${e.company_name || ""} · ${e.title || ""}`}
                        >
                          <Icon className="h-3 w-3 shrink-0" />
                          <span className="truncate flex-1">
                            {new Date(e.at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })} · {e.title || "(sin título)"}
                          </span>
                        </button>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <button
                        onClick={() => setSelectedEvent({ __day: d, __events: dayEvents })}
                        className="text-[10px] text-stone-500 hover:text-brand-emerald-dark"
                      >
                        +{dayEvents.length - 3} más
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Móvil: agenda */}
        <div className="md:hidden space-y-3" data-testid="xcc-agenda-mobile">
          {(() => {
            const monthDays = days.filter((d) => d.getMonth() === cursor.getMonth());
            const withEvents = monthDays.filter((d) => {
              const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
              return (eventsByDay.get(key) || []).length > 0;
            });
            if (withEvents.length === 0) {
              return (
                <div className="card-elevated p-10 text-center text-stone-500 text-sm">
                  Sin eventos este mes en las empresas visibles.
                </div>
              );
            }
            return withEvents.map((d) => {
              const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
              const dayEvents = eventsByDay.get(key) || [];
              const isToday = sameDay(d, today);
              return (
                <div key={key} className="card-elevated overflow-hidden">
                  <div className={`px-4 py-2 border-b border-border flex items-center gap-2 ${isToday ? "bg-brand-gold-soft/50" : "bg-stone-50/60"}`}>
                    <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">
                      {d.toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "short" })}
                    </div>
                    {isToday && <Badge className="bg-brand-gold text-brand-ink border-brand-gold-dark text-[10px]">Hoy</Badge>}
                  </div>
                  <div className="divide-y divide-border">
                    {dayEvents.map((e) => {
                      const Icon = PLATFORM_ICON[e.platform] || Mail;
                      const color = e.company_color || "#00A36C";
                      return (
                        <button
                          key={e.id}
                          onClick={() => setSelectedEvent(e)}
                          className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-stone-50"
                          data-testid={`xcc-agenda-event-${e.id}`}
                        >
                          <div className="h-9 w-9 rounded-md flex items-center justify-center text-white shrink-0" style={{ backgroundColor: color }}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-brand-ink line-clamp-1">{e.title || "(sin título)"}</div>
                            <div className="text-xs text-stone-500">
                              {new Date(e.at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })} · {e.company_name}
                            </div>
                          </div>
                          <Badge className={`border font-semibold text-[10px] ${STATUS_STYLES[e.status] || ""}`}>
                            {STATUS_LABELS[e.status] || e.status}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
        </>
      )}

      <Dialog open={!!selectedEvent} onOpenChange={(v) => !v && setSelectedEvent(null)}>
        <DialogContent data-testid="xcc-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {selectedEvent?.__events
                ? `Eventos del ${selectedEvent.__day?.toLocaleDateString("es-ES", { dateStyle: "long" })}`
                : "Detalle del evento"}
            </DialogTitle>
            <DialogDescription>
              {selectedEvent?.__events
                ? `${selectedEvent.__events.length} eventos programados este día.`
                : "Información y enlace directo al módulo."}
            </DialogDescription>
          </DialogHeader>
          {selectedEvent?.__events ? (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {selectedEvent.__events.map((e) => <XCCRow key={e.id} evt={e} />)}
            </div>
          ) : selectedEvent && (
            <XCCDetail evt={selectedEvent} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function XCCRow({ evt }) {
  const Icon = PLATFORM_ICON[evt.platform] || Mail;
  return (
    <div className="rounded-lg border border-border p-3 flex items-center gap-3">
      <div className="h-9 w-9 rounded-md flex items-center justify-center text-white shrink-0" style={{ backgroundColor: evt.company_color || "#00A36C" }}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-brand-ink line-clamp-1 font-medium">{evt.title || evt.name}</div>
        <div className="text-xs text-stone-500 flex items-center gap-2">
          <Badge className={`border font-semibold ${STATUS_STYLES[evt.status] || ""}`}>
            {STATUS_LABELS[evt.status] || evt.status}
          </Badge>
          <span>{evt.company_name}</span>
        </div>
      </div>
    </div>
  );
}

function XCCDetail({ evt }) {
  const Icon = PLATFORM_ICON[evt.platform] || Mail;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: evt.company_color || "#00A36C" }}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="font-semibold text-brand-ink">{evt.company_name || "Empresa"}</div>
          <div className="text-xs text-stone-500 capitalize">{evt.platform}</div>
          <Badge className={`border font-semibold mt-1 ${STATUS_STYLES[evt.status] || ""}`}>
            {STATUS_LABELS[evt.status] || evt.status}
          </Badge>
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Contenido / Asunto</div>
        <p className="text-sm text-brand-ink whitespace-pre-wrap">{evt.title || evt.name || "—"}</p>
      </div>
      {evt.author_name && (<div className="text-xs text-stone-500">Autor: <strong>{evt.author_name}</strong></div>)}
      {evt.at && (<div className="text-xs text-stone-500">Fecha: {new Date(evt.at).toLocaleString("es-ES", { dateStyle: "long", timeStyle: "short" })}</div>)}
      <div className="text-xs text-stone-500 italic">
        Sugerencia: para operar sobre este evento cambie primero a la empresa <strong>{evt.company_name}</strong> en el selector de la cabecera.
      </div>
      {evt.target_url && (
        <div className="pt-3">
          <Link to={evt.target_url}>
            <Button className="bg-brand-emerald text-white hover:bg-brand-emerald-dark" data-testid="xcc-event-goto">
              <ExternalLink className="h-4 w-4 mr-2" /> Ir al módulo
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
